/**
 * Demo scenarios for the Content tree, picked from the profile menu's Demo
 * state: the full catalog, a small start for Leo to build on, or nothing yet.
 * Standards are left whole in every scenario; only content changes.
 */

import { COURSE_CONTENT_DIMENSION_ID, PT_COURSE_CONTENT_DIMENSION_ID } from "./dimensions"
import type { ExamCategory } from "./types"

export type ClassificationScenario = "full" | "minimal" | "empty"

export const CLASSIFICATION_SCENARIO_STORE_KEY = "shell:classification-scenario:v1"

export const CLASSIFICATION_SCENARIO_OPTIONS: ReadonlyArray<{
  id: ClassificationScenario
  label: string
  hint: string
}> = [
  { id: "full", label: "Full", hint: "Every domain and skill; nearly every question tagged" },
  { id: "minimal", label: "Minimal", hint: "Three domains, two skills each; Leo fills the gaps" },
  { id: "empty", label: "Empty", hint: "No content categories yet" },
]

const CONTENT_DIMENSIONS = new Set([COURSE_CONTENT_DIMENSION_ID, PT_COURSE_CONTENT_DIMENSION_ID])
const MINIMAL_DOMAINS = 3
const MINIMAL_SKILLS = 2

const bySortOrder = (a: ExamCategory, b: ExamCategory) => a.sortOrder - b.sortOrder

/** The full catalog cut down to the scenario. */
export function categoriesForScenario(full: ExamCategory[], scenario: ClassificationScenario): ExamCategory[] {
  if (scenario === "full") return full
  const contentRoots = full.filter(
    (c) => c.parentId === null && CONTENT_DIMENSIONS.has(c.dimensionId ?? COURSE_CONTENT_DIMENSION_ID),
  )
  const rootIds = new Set(contentRoots.map((c) => c.id))
  const parentOf = new Map(full.map((c) => [c.id, c.parentId]))
  const rootOf = (id: string): string => {
    let at = id
    while (parentOf.get(at)) at = parentOf.get(at)!
    return at
  }
  const isContent = (c: ExamCategory) => rootIds.has(rootOf(c.id))
  if (scenario === "empty") return full.filter((c) => !isContent(c))

  const keep = new Set<string>()
  const byDimension = new Map<string, ExamCategory[]>()
  for (const root of contentRoots) {
    const key = root.dimensionId ?? COURSE_CONTENT_DIMENSION_ID
    byDimension.set(key, [...(byDimension.get(key) ?? []), root])
  }
  for (const roots of byDimension.values()) {
    for (const root of [...roots].sort(bySortOrder).slice(0, MINIMAL_DOMAINS)) {
      keep.add(root.id)
      full
        .filter((c) => c.parentId === root.id)
        .sort(bySortOrder)
        .slice(0, MINIMAL_SKILLS)
        .forEach((skill) => keep.add(skill.id))
    }
  }
  return full.filter((c) => !isContent(c) || keep.has(c.id))
}
